import type { KeyboardEvent, MouseEvent } from 'react';
import type { TextStyle } from 'react-native';

import type { CvtConcernOption } from '../cvt-primary-concerns-modal.types';

import { colors } from '@packages/core/styles';
import { Icon, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';

const titleStyle: TextStyle = { textTransform: 'none' };

const useStyles = createUseStyles({
    chip: {
        alignItems: 'center',
        backgroundColor: colors.blueFour,
        borderRadius: 4,
        display: 'inline-flex',
        gap: 4,
        height: 20,
        padding: '2px 4px',
        '&:focus': {
            boxShadow: '0 0 0 0.2rem rgba(38, 143, 255, 0.5)',
            outline: 'none',
        },
    },
    removeButton: {
        alignItems: 'center',
        backgroundColor: 'transparent',
        border: 0,
        display: 'inline-flex',
        padding: 0,
    },
});

export type CvtConcernsMultiSelectChipProps = {
    /** Removes the option from the selected options. */
    onRemove: (option: CvtConcernOption) => void;

    /** The selected option. */
    option: CvtConcernOption;
};

/**
 * A selected option, shown as a chip in the concerns multi-select's field.
 *
 * Renders the option's title and a button that removes it. Backspace removes it too while the chip has focus.
 */
export function CvtConcernsMultiSelectChip({ onRemove, option }: CvtConcernsMultiSelectChipProps) {
    const classes = useStyles();

    /**
     * Focuses the chip, since the multi-select stops a click from focusing it. Stops the click from reaching the field,
     * which would focus the combobox and open or close the menu.
     */
    function handleChipClick(event: MouseEvent<HTMLSpanElement>) {
        event.stopPropagation();
        event.currentTarget.focus();
    }

    /** Removes the option when Backspace is pressed on the focused chip. */
    function handleChipKeyDown(event: KeyboardEvent<HTMLSpanElement>) {
        if (event.key !== 'Backspace') {
            return;
        }

        event.preventDefault();
        onRemove(option);
    }

    /** Removes the option, and stops the click there so it doesn't also focus the chip or toggle the menu. */
    function handleRemoveButtonClick(event: MouseEvent<HTMLButtonElement>) {
        event.stopPropagation();
        onRemove(option);
    }

    return (
        <span className={classes.chip} onClick={handleChipClick} onKeyDown={handleChipKeyDown} tabIndex={0}>
            <Typography color="blueOne" style={titleStyle} variant="caption6">
                {option.title}
            </Typography>
            <button
                aria-label={`Remove ${option.title}`}
                className={classes.removeButton}
                onClick={handleRemoveButtonClick}
                tabIndex={-1}
                type="button"
            >
                <Icon color="blueOne" name="x-close" size={16} />
            </button>
        </span>
    );
}
